import React, { useEffect } from "react";
import "./../components/Report.scss";
import { _axiosCall } from "../../../../../../shared/helpers/apicalls";

import RentalTypePage from "./../components/RentalTypePage";
import CoverPage from "./../components/CoverPage";
import {
  openAIModel,
  _reportScopePrompt,
} from "../../../exportreport/utils";
import {
  SE_CORE,
  SE_FL,
  SE_BEDROOMS,
  SE_SEGMENTS,
  SE_COMPARISON_EXCLUDE,
  _seCandidateSlugs,
  _seRequestedSlug,
  _sePickArea,
  _sePriorYearMonth,
  _seMonthForDate,
  _seIntervalForGap,
  _seBucketSeries,
  _seSelectBucket,
  _sePriorPeriodStats,
  _sePeriodLabel,
  _sePriorBucketKey,
  _seBucketLabel,
  _seAggregate,
  _seAreaLabel,
  _sePctChange,
} from "../../../../../../shared/helpers/streetEasy";

const apiURL = process.env.API_ROOT;

/**
 * ExportModal mounts the report twice while `reportData` is null — once as the editor's
 * `contentHTML` and again directly (`{!reportData && ReportContent}`), which is exactly the state
 * every filter change produces. Both copies run their own effects, so a single click fired two
 * Solr queries and, more expensively, two OpenAI generations that could return different copy.
 *
 * These module-scope maps let the second copy join the first's work instead of repeating it,
 * keyed by what was actually requested. Module scope is deliberate: the two copies are separate
 * component instances, so a ref inside the component cannot see across them.
 */
const inFlightLoads = {};
const inFlightAi = {};

const _dedupe = (store, key, run) => {
  if (store[key]) return store[key];
  store[key] = Promise.resolve()
    .then(run)
    .then(
      (value) => { delete store[key]; return value; },
      (error) => { delete store[key]; throw error; }
    );
  return store[key];
};

// Five years of monthly points: enough to show the 2021 reset and the run-up since without
// crowding the axis.
const CHART_YEARS = 5;

const BlendedLayout = (props) => {
  const {
    fetchInProgress,
    setFetchInProgress,
    setReportLoaded,
    reportData,
    setReportData,
    searchTags,
    summarizeGeographies,
    customDate,
    submit,
    setSubmit,
    setAiResponse,
    setFetchAi,
    fetchAi,
    setAIInProgress,
  } = props;

  const { dateOptions } = customDate;

  useEffect(() => {
    if (!reportData && !fetchInProgress && submit) {
      loadData();
      setSubmit(false);
    }
  }, [submit]);

  useEffect(() => {
    const fetchAIData = async () => {
      setFetchAi && setFetchAi(false);
      setAIInProgress(true);
      const aiResponse = await handleAIGenerate(reportData);
      setAiResponse && setAiResponse(aiResponse);
      setAIInProgress(false);
    };

    if (reportData && fetchAi) {
      fetchAIData();
    }
  }, [reportData, fetchAi]);

  /**
   * One query, then shape it. Every candidate geography's whole series comes back at once and the
   * most specific area that actually has data wins — cheaper than resolving the area first and
   * fetching its series second, and it makes the fallback to borough/city automatic.
   */
  const loadData = async () => {
    setFetchInProgress(true);

    const candidates = _seCandidateSlugs(searchTags);
    // The five boroughs and the city always come along: they are the comparison ladder the pages
    // put their own area against, and the citywide borough chart had no rows to draw without them.
    const fetchSlugs = candidates
      .concat(["manhattan", "brooklyn", "queens", "bronx", "staten-island", "nyc"])
      .filter((slug, i, all) => all.indexOf(slug) === i);
    const endMonth = _seMonthForDate(dateOptions && dateOptions.endDate);
    const startYear = parseInt(endMonth.slice(0, 4), 10) - CHART_YEARS;

    const axiosData = {
      url: apiURL + "/query/" + SE_CORE + "?cache=60",
      method: "post",
      query: {
        q: `{!terms f=area_slug}${fetchSlugs.join(",")}`,
        fq: [`month:[${startYear}-01-01T00:00:00Z TO ${endMonth}-01T23:59:59Z]`],
        fl: SE_FL,
        sort: "month asc",
        rows: 1000,
        wt: "json",
      },
    };

    const loadKey = JSON.stringify([fetchSlugs, endMonth, dateOptions && dateOptions.gap, dateOptions && dateOptions.periodMode]);
    await _dedupe(inFlightLoads, loadKey, () => _axiosCall(axiosData))
      .then((res) => {
        const docs = (res && res.docs) || [];
        const area = _sePickArea(docs, candidates);
        if (!area) {
          setFetchInProgress(false);
          setReportLoaded(true);
          setSubmit(false);
          setReportData({ area: false, months: [], current: false, priorYear: false });
          return res;
        }

        const months = docs.filter((d) => d.area_slug === area.area_slug);

        // The picker offers month / quarter / year, so the monthly snapshots are bucketed to the
        // chosen interval and averaged. Prior-year figures use the SAME calendar months, so a
        // part-finished quarter or year is never set against a complete one.
        const interval = _seIntervalForGap(dateOptions && dateOptions.gap);
        const series = _seBucketSeries(months.filter((d) => d.month_str <= endMonth), interval);
        // "current" = newest, part-finished period (QTD / YTD); "complete" = newest period that
        // ran its full length (2Q 2026 / 2025).
        const currentBucket = _seSelectBucket(series, dateOptions && dateOptions.periodMode);
        const current = currentBucket ? currentBucket.stats : false;
        const priorYear = currentBucket ? _sePriorPeriodStats(currentBucket.months, months) : false;

        // A bedroom page with no published median renders as a page of "--". StreetEasy covers
        // every bedroom cut for the city and boroughs but not for every neighborhood (118 of 176
        // publish a studio median), so pages are built from what the area actually has.
        const segments = SE_SEGMENTS.filter(
          (seg) => seg.key === "all_rentals" || (current && current[`median_asking_rent_${seg.field}`] > 0)
        );

        // What this area is measured against. A borough or citywide report compares boroughs; a
        // neighborhood compares itself with its borough and the city, which is the ladder a reader
        // actually wants and which the page cannot show anywhere else.
        const periodMonths = currentBucket ? currentBucket.months.map((d) => d.month_str) : [];
        const priorMonths = periodMonths.map((m) => _sePriorYearMonth(m));
        const docFor = (slug, wanted) =>
          _seAggregate(docs.filter((d) => d.area_slug === slug && wanted.indexOf(d.month_str) >= 0));
        const ladderSlugs = (area.area_type === "neighborhood"
          ? [area.area_slug, area.borough ? area.borough.replace(/ /g, "-") : "", "nyc"]
          : ["manhattan", "brooklyn", "queens", "bronx", "staten-island"]
        ).filter((slug) => slug === area.area_slug || SE_COMPARISON_EXCLUDE.indexOf(slug) < 0);
        const comparison = ladderSlugs
          .filter(Boolean)
          .map((slug) => ({
            slug: slug,
            current: docFor(slug, periodMonths),
            prior: docFor(slug, priorMonths),
          }))
          .filter((row) => row.current);

        setFetchInProgress(false);
        setReportLoaded(true);
        setSubmit(false);
        setReportData({
          area: area,
          segments: segments,
          comparison: comparison,
          areaLabel: _seAreaLabel(area),
          months: months,
          current: current || false,
          priorYear: priorYear,
          interval: interval,
          series: series,
          currentMonthLabel: _sePeriodLabel(currentBucket),
          priorMonthLabel: currentBucket && priorYear ? _sePeriodLabel({
            key: _sePriorBucketKey(currentBucket.key),
            label: _seBucketLabel(_sePriorBucketKey(currentBucket.key)),
            months: currentBucket.months,
          }) : "",
          // Averaged periods are not medians of the period; the page says so where it matters.
          isAveraged: !!current && current._months > 1,
          // A search StreetEasy cannot answer at the requested level is reported at the level it
          // can — say so, rather than let a Carnegie Hill search read as Carnegie Hill data.
          requestedMoreSpecific: !!_seRequestedSlug(searchTags) && _seRequestedSlug(searchTags) !== area.area_slug,
        });
        setFetchAi && setFetchAi(true);
        return res;
      })
      .catch((error) => {
        console.log("error: " + error);
        setFetchInProgress(false);
        setReportLoaded(true);
        setSubmit(false);
      });
  };

  /**
   * The model gets the published figures and nothing else. Anything absent from this object is
   * absent from the data, and it is told to say so rather than reach for a number.
   */
  const handleAIGenerate = async (data) => {
    if (!data || !data.current) return null;

    const cur = data.current;
    const prior = data.priorYear;
    const segmentKeys = (data.segments || SE_SEGMENTS).map((seg) => seg.key);
    const stat = (field) => ({
      current: cur[field] != null ? cur[field] : null,
      prior_year: prior && prior[field] != null ? prior[field] : null,
      pct_change: prior ? _sePctChange(cur[field], prior[field]) : null,
    });

    const payload = {
      geography: data.areaLabel,
      period: data.currentMonthLabel,
      prior_year_period: data.priorMonthLabel,
      figures_are: data.isAveraged
        ? "averages of the monthly values in the period (rents are averages of monthly medians, supply is average listings on the market, never a sum)"
        : "a single monthly snapshot",
      median_rent: stat("median_asking_rent_all"),
      supply: stat("rental_inventory_all"),
      share_of_listings_with_a_price_cut: stat("discount_share_all"),
      by_bedroom: SE_BEDROOMS.filter((bed) => segmentKeys.indexOf(bed.key) >= 0).map((bed) => ({
        segment: bed.key,
        bedrooms: bed.label,
        median_rent: stat(`median_asking_rent_${bed.key}`),
        supply: stat(`rental_inventory_${bed.key}`),
      })),
      trend_median_rent: data.months
        .filter((m) => m.median_asking_rent_all > 0)
        .map((m) => ({ month: m.month_str, median_rent: m.median_asking_rent_all })),
    };

    const prompt = `Give me an analysis of the RENTAL market in ${data.areaLabel} for ${data.currentMonthLabel}.

    All figures are MONTHLY ASKING RENTS from StreetEasy's published market data, never sale prices and never signed-lease rents. Every number describes listings on the market, so describe rents and supply, not leasing volume or deals closed. When the period covers more than one month the figures are averages of the monthly values — never call an averaged figure a median for the period. Call the figures "median rent" and "supply" in the copy — the report labels them that way — and never write "inventory".

    Focus on price: the median rent, how it compares with ${data.priorMonthLabel}, how rents differ by bedroom count, and what the share of listings with a price cut says about negotiating room. Mention supply only as context for the price story.

    ${_reportScopePrompt(searchTags, summarizeGeographies)}

    Quote the figures below exactly as given; never estimate, round further, or introduce a number that is not here. Where a value is null, say the data is not published rather than inferring it. Percent changes are already computed — use them rather than recomputing.

    ${JSON.stringify(payload)}

    Write one analysis per segment: all_rentals covers the whole market, and studio, one_bd, two_bd and three_plus_bd each cover that bedroom size only. In a bedroom analysis, write about that size and how it compares with the market as a whole — never repeat the market-wide analysis. Each is 4 to 5 sentences, easy to understand for consumer audiences, plain text. Format rents as $#,### per month and shares as whole percentages. Return valid raw JSON, no formatting, with exactly these keys and no others: ${JSON.stringify(segmentKeys)} — each mapping to { "rents": "analysis in text" }.`;

    const queryData = {
      messages: [
        {
          role: "system",
          content: `You are a NYC real estate expert that provides insightful data-driven analysis that is easy to understand. You speak in a professional yet friendly manner. Think TV news analysts.`,
        },
        { role: "user", content: prompt },
      ],
      model: openAIModel,
      response_format: { type: "json_object" },
    };

    let aiResponse = null;
    try {
      const aiKey = JSON.stringify([data.areaLabel, data.currentMonthLabel, segmentKeys]);
      const res = await _dedupe(inFlightAi, aiKey, () =>
        _axiosCall({ url: apiURL + "/ai/chat", method: "post", query: queryData })
      );
      if (res && res.choices && res.choices[0] && res.choices[0].message) {
        aiResponse = JSON.parse(res.choices[0].message.content);
      }
    } catch (error) {
      console.log("Error generating summary:", error);
    }
    return aiResponse;
  };

  if (!reportData) return null;

  return (
    <>
      <CoverPage
        {...props}
        summarizeGeographies={summarizeGeographies}
        priorYearPeriodName={reportData.priorMonthLabel}
        currentPeriodName={reportData.currentMonthLabel}
      />
      {(reportData.segments || SE_SEGMENTS).map((segment) => (
        <RentalTypePage
          key={segment.key}
          segment={segment}
          {...props}
          summarizeGeographies={summarizeGeographies}
          priorYearPeriodName={reportData.priorMonthLabel}
          currentPeriodName={reportData.currentMonthLabel}
        />
      ))}
    </>
  );
};

export default BlendedLayout;
